import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Eye, EyeOff, Shield } from 'lucide-react';
import { toast } from 'sonner';
import OtpVerifyStep from '../../components/OtpVerifyStep';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await adminAPI.login(email, password);
      const data = response.data;
      
      if (data.requires_2fa) {
        setNeeds2FA(true);
        setTempToken(data.temp_token);
        setLoading(false);
        return;
      }
      
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      
      if (data.needs_2fa_setup) {
        toast.success('Please set up 2FA for admin security.');
      } else {
        toast.success('Welcome, Admin!');
      }
      navigate('/gmbadmin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (code, trustDevice, trustDays) => {
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/auth/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, code, trust_device: trustDevice, trust_days: trustDays })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      if (data.trusted_device_token) {
        localStorage.setItem('admin_trusted_device', data.trusted_device_token);
      }
      toast.success('Welcome, Admin!');
      navigate('/gmbadmin/dashboard');
    } catch (err) {
      toast.error(err.message || 'Invalid code');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#202124] flex items-center justify-center p-8">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#1A73E8] rounded-xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
            GMB Autopilot Admin
          </h1>
          <p className="text-[#9AA0A6] mt-2">Sign in to access admin panel</p>
        </div>

        <div className="bg-white rounded-xl p-8">
          {needs2FA ? (
            <OtpVerifyStep
              onVerify={handle2FAVerify}
              onCancel={() => { setNeeds2FA(false); setTempToken(''); }}
              loading={loading}
            />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#202124] mb-2">Admin Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-google" placeholder="Enter admin email" required data-testid="admin-email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#202124] mb-2">Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="input-google pr-12" placeholder="Enter password" required data-testid="admin-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5F6368]">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-google w-full" data-testid="admin-login-btn">
                {loading ? 'Signing in...' : 'Sign In as Admin'}
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-[#9AA0A6] text-sm mt-6">Authorized personnel only</p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
